<template>
  <div class="customers-page">
    <v-card class="customers-card rounded-xl pa-4 d-flex flex-column" variant="flat" :loading="listLoading">
      <div class="toolbar-top d-flex flex-wrap align-center ga-4 mb-4 flex-shrink-0">
        <div class="search-item">
          <span class="search-label">客户类型</span>
          <v-select v-model="typeFilter" :items="typeFilterItems" item-title="label" item-value="value"
            placeholder="全部类型" variant="outlined" density="compact" hide-details clearable class="search-input--select"
            rounded="xl" />
        </div>
        <div class="search-item">
          <span class="search-label">关键字</span>
          <v-text-field v-model="searchInput" placeholder="按客户名称/简称搜索" variant="outlined" density="compact" hide-details
            clearable class="search-input" rounded="xl" @keyup.enter="handleSearch" />
        </div>

        <div class="search-actions d-flex ga-3">
          <v-btn v-hasPermi="['ops:customer:query']" color="#2655f1" rounded="xl" class="action-btn"
            @click="handleSearch">
            <v-icon start icon="mdi-magnify" />查询
          </v-btn>
          <v-btn v-hasPermi="['ops:customer:export']" variant="outlined" color="#2655f1" rounded="xl" class="action-btn"
            :loading="exportLoading" @click="handleExport">
            <v-icon start icon="mdi-download" />导出
          </v-btn>
          <v-btn variant="outlined" color="#2655f1" rounded="xl" class="action-btn" @click="resetFilters">
            <v-icon start icon="mdi-refresh" />重置
          </v-btn>
        </div>

        <div class="toolbar-actions d-flex ga-3">
          <v-btn v-hasPermi="['ops:customer:add']" color="#2655f1" rounded="xl" class="action-btn"
            @click="openAddDialog">
            <v-icon start icon="mdi-plus" />新增客户
          </v-btn>
        </div>
      </div>

      <div class="table-wrap">
        <div v-if="customerList.length" class="customers-table-host">
          <v-table fixed-header height="100%" class="gm-table customers-table gm-border rounded-lg"
            density="comfortable">
            <thead>
              <tr>
                <th class="text-no-wrap">客户类型</th>
                <th>客户名称</th>
                <th>简称</th>
                <th class="text-no-wrap">联系人</th>
                <th class="text-no-wrap">电话</th>
                <th class="text-no-wrap">邮箱</th>
                <th class="text-no-wrap">地址</th>
                <th class="text-no-wrap">创建时间</th>
                <th v-if="hasRowActions" class="text-center text-no-wrap sticky-action-col">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in customerList" :key="row.id || index">
                <td>
                  <v-chip size="small" variant="tonal" :color="customerTypeColor(row.customerType)" label>
                    {{ customerTypeText(row.customerType) }}
                  </v-chip>
                </td>
                <td>
                  <div class="cell-name">{{ row.name }}</div>
                  <div v-if="row.id" class="text-caption text-medium-emphasis">ID：{{ row.id }}</div>
                </td>
                <td>{{ row.shortName || '—' }}</td>
                <td class="cell-contact text-body-2">{{ row.contactPerson || '—' }}</td>
                <td class="cell-contact text-body-2">{{ row.phone || '—' }}</td>
                <td class="cell-email text-body-2 text-medium-emphasis">{{ row.email || '—' }}</td>
                <td>
                  <div class="cell-address text-body-2 text-medium-emphasis">{{ row.address || '—' }}</div>
                </td>
                <td class="text-body-2 text-medium-emphasis">{{ row.createTime || '—' }}</td>
                <td v-if="hasRowActions" class="sticky-action-col text-center">
                  <div class="action-buttons justify-center">
                    <v-btn v-hasPermi="['ops:customer:query']" variant="text" color="#2655f1" rounded="xl"
                      density="comfortable" @click="openDetail(row)">详情</v-btn>
                    <v-btn v-hasPermi="['ops:customer:edit']" variant="text" color="#2655f1" rounded="xl"
                      density="comfortable" @click="openEditDialog(row)">编辑</v-btn>
                    <v-btn v-hasPermi="['ops:customer:delete']" variant="text" color="#FF6A46" rounded="xl"
                      density="comfortable" @click="handleDelete(row)">删除</v-btn>
                  </div>
                </td>
              </tr>
            </tbody>
          </v-table>
        </div>
        <div v-else class="empty-tip empty-tip--fill text-center py-10 text-medium-emphasis">
          {{ emptyHint }}
        </div>
      </div>

      <TablePagination v-model="page" v-model:page-size="pageSize" class="customers-pagination flex-shrink-0"
        :total="total" :has-sizes="true" :has-jump="true" @change="fetchList" />
    </v-card>

    <v-dialog v-model="formDialogVisible" max-width="840px" persistent class="gm-dialog">
      <v-card class="rounded-30 overflow-hidden dialog-card">
        <div class="gm-dialog-header d-flex align-center px-8 py-6">
          <div class="text-h5">
            <span style="color: #2655f1">{{ dialogMode === 'add' ? '新增' : '修改' }}</span><span>客户</span>
          </div>
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" color="rgba(46, 46, 46, 0.5)" @click="closeFormDialog" />
        </div>
        <v-card-text class="pa-8 pt-4 pb-4 scrollbar-hide" style="max-height: 88vh; overflow-y: auto">
          <v-form>
            <div class="form-grid-2">
              <div class="form-group">
                <label>客户类型 <span class="required-star">*</span></label>
                <v-select v-model="form.customerType" :items="customerTypeMap.toOptions()" item-title="label"
                  item-value="value" placeholder="请选择客户类型" variant="outlined" density="compact" class="gm-input"
                  hide-details />
              </div>
              <div class="form-group">
                <label>客户名称 <span class="required-star">*</span></label>
                <v-text-field v-model="form.customerName" placeholder="请输入客户名称" variant="outlined" density="compact"
                  class="gm-input" hide-details />
              </div>
              <div class="form-group">
                <label>简称</label>
                <v-text-field v-model="form.shortName" placeholder="请输入简称" variant="outlined" density="compact"
                  class="gm-input" hide-details />
              </div>
              <div class="form-group">
                <label>联系人</label>
                <v-text-field v-model="form.contactPerson" placeholder="请输入联系人" variant="outlined" density="compact"
                  class="gm-input" hide-details />
              </div>
              <div class="form-group">
                <label>电话</label>
                <v-text-field v-model="form.phone" placeholder="请输入电话" variant="outlined" density="compact"
                  class="gm-input" hide-details />
              </div>
              <div class="form-group">
                <label>邮箱</label>
                <v-text-field v-model="form.email" placeholder="请输入邮箱" variant="outlined" density="compact"
                  class="gm-input" hide-details />
              </div>
              <div class="form-group grid-full">
                <label>地址</label>
                <v-text-field v-model="form.address" placeholder="请输入地址" variant="outlined" density="compact"
                  class="gm-input" hide-details />
              </div>
              <div class="form-group grid-full">
                <label>备注</label>
                <v-textarea v-model="form.remark" placeholder="请输入备注" variant="outlined" density="compact"
                  class="gm-input" hide-details auto-grow rows="3" />
              </div>
            </div>
          </v-form>
        </v-card-text>
        <v-card-actions class="px-8 pb-8 pt-0 justify-end">
          <v-btn variant="flat" color="#D9D9D9" class="gm-btn-secondary px-10 mr-4" @click="closeFormDialog">
            取消
          </v-btn>
          <v-btn color="#2655f1" variant="flat" class="gm-btn-primary px-10 text-white" :loading="formSubmitting"
            @click="submitForm">
            确定
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialogVisible" max-width="420px" persistent class="gm-dialog">
      <v-card class="rounded-30 overflow-hidden dialog-card">
        <div class="gm-dialog-header d-flex align-center px-8 py-6">
          <div class="text-h5"><span style="color: #2655f1">删除</span><span>确认</span></div>
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" color="rgba(46, 46, 46, 0.5)" @click="closeDeleteDialog" />
        </div>
        <v-card-text class="pa-8 pt-2 pb-4">
          <div class="text-body-1">{{ deleteDialogMessage }}</div>
        </v-card-text>
        <v-card-actions class="px-8 pb-8 pt-0 justify-end">
          <v-btn variant="flat" color="#D9D9D9" class="gm-btn-secondary px-10 mr-4" @click="closeDeleteDialog">
            取消
          </v-btn>
          <v-btn color="#2655f1" variant="flat" class="gm-btn-primary px-10 text-white" :loading="deleteSubmitting"
            @click="doDelete">
            确定
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="detailDialogVisible" max-width="720px" persistent class="gm-dialog">
      <v-card class="rounded-30 overflow-hidden dialog-card">
        <div class="gm-dialog-header d-flex align-center px-8 py-6">
          <div class="text-h5"><span style="color: #2655f1">客户</span><span>详情</span></div>
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" color="rgba(46, 46, 46, 0.5)" @click="closeDetailDialog" />
        </div>
        <v-card-text class="pa-8 pt-4 pb-4">
          <div v-if="detailTarget" class="detail-grid">
            <div class="detail-item">
              <div class="detail-label">客户类型</div>
              <div class="detail-value">
                <v-chip size="small" variant="tonal" :color="customerTypeColor(detailTarget.customerType)" label>
                  {{ customerTypeText(detailTarget.customerType) }}
                </v-chip>
              </div>
            </div>
            <div class="detail-item">
              <div class="detail-label">客户名称</div>
              <div class="detail-value">{{ detailTarget.name || '—' }}</div>
            </div>
            <div class="detail-item">
              <div class="detail-label">简称</div>
              <div class="detail-value">{{ detailTarget.shortName || '—' }}</div>
            </div>
            <div class="detail-item">
              <div class="detail-label">联系人</div>
              <div class="detail-value">{{ detailTarget.contactPerson || '—' }}</div>
            </div>
            <div class="detail-item">
              <div class="detail-label">电话</div>
              <div class="detail-value">{{ detailTarget.phone || '—' }}</div>
            </div>
            <div class="detail-item">
              <div class="detail-label">邮箱</div>
              <div class="detail-value">{{ detailTarget.email || '—' }}</div>
            </div>
            <div class="detail-item detail-item--full">
              <div class="detail-label">地址</div>
              <div class="detail-value detail-value--multiline">{{ detailTarget.address || '—' }}</div>
            </div>
            <div class="detail-item detail-item--full">
              <div class="detail-label">备注</div>
              <div class="detail-value detail-value--multiline">{{ detailTarget.remark || '—' }}</div>
            </div>
            <div class="detail-item detail-item--full">
              <div class="detail-label">创建时间</div>
              <div class="detail-value">{{ detailTarget.createTime || '—' }}</div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="px-8 pb-8 pt-0 justify-end">
          <v-btn color="#2655f1" variant="flat" class="gm-btn-primary px-10 text-white" @click="closeDetailDialog">
            关闭
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import dayjs from 'dayjs'
import { eui } from 'ems-ui'
import TablePagination from '@/components/TablePagination/index.vue'
import * as customerApi from '@/api/device/customer'
import { createStatusMap } from '@/utils'
import { checkPermi } from '@/utils/permission'

const CUSTOMER_TYPE_INVESTOR = '投资方'
const CUSTOMER_TYPE_PROPRIETOR = '业主方'
const CUSTOMER_TYPE_CHANNEL = '渠道方'

const customerTypeMap = createStatusMap({
  [CUSTOMER_TYPE_INVESTOR]: { text: CUSTOMER_TYPE_INVESTOR, color: 'primary' },
  [CUSTOMER_TYPE_PROPRIETOR]: { text: CUSTOMER_TYPE_PROPRIETOR, color: 'success' },
  [CUSTOMER_TYPE_CHANNEL]: { text: CUSTOMER_TYPE_CHANNEL, color: 'warning' },
})

const typeFilterItems = [
  { label: '全部', value: '' },
  ...customerTypeMap.toOptions(),
]

const defaultForm = () => ({
  customerType: CUSTOMER_TYPE_INVESTOR,
  customerName: '',
  shortName: '',
  contactPerson: '',
  phone: '',
  email: '',
  address: '',
  remark: '',
})

const listLoading = ref(false)
const customerList = ref([])
const total = ref(0)
const page = ref(1)
const pageSize = ref(10)
const typeFilter = ref('')
const searchKeyword = ref('')
const searchInput = ref('')
const formDialogVisible = ref(false)
const dialogMode = ref('add')
const formSubmitting = ref(false)
const editingCustomerId = ref(null)
const form = reactive(defaultForm())
const deleteDialogVisible = ref(false)
const deleteSubmitting = ref(false)
const deleteTarget = ref(null)
const detailDialogVisible = ref(false)
const detailTarget = ref(null)
const exportLoading = ref(false)

const hasRowActions = computed(() => checkPermi(['ops:customer:query']) || checkPermi(['ops:customer:edit']) || checkPermi(['ops:customer:delete']))

const emptyHint = computed(() => {
  if (searchKeyword.value || typeFilter.value) return '当前筛选条件下暂无客户'
  return '暂无客户数据'
})

function normalizeCustomerType(value) {
  const raw = String(value ?? '').trim()
  if (!raw) return ''
  if ([CUSTOMER_TYPE_INVESTOR, 'investor', '1'].includes(raw)) return CUSTOMER_TYPE_INVESTOR
  if ([CUSTOMER_TYPE_PROPRIETOR, 'proprietor', '2'].includes(raw)) return CUSTOMER_TYPE_PROPRIETOR
  if ([CUSTOMER_TYPE_CHANNEL, 'channel', '3'].includes(raw)) return CUSTOMER_TYPE_CHANNEL
  return raw
}

function customerTypeText(value) {
  const type = normalizeCustomerType(value)
  return customerTypeMap.getText(type, type || '—')
}

function customerTypeColor(value) {
  const type = normalizeCustomerType(value)
  return customerTypeMap.getColor(type, 'info')
}

function formatDateTime(value) {
  if (value == null || value === '') return ''
  const date = dayjs(value)
  return date.isValid() ? date.format('YYYY-MM-DD HH:mm') : String(value)
}

function normalizeCustomerRow(item, index) {
  return {
    id: item?.id ?? item?.customerId ?? item?.customerID ?? item?.uuid ?? `row-${index}`,
    name: item?.customerName ?? item?.name ?? '',
    shortName: item?.shortName ?? item?.customerShortName ?? item?.abbr ?? '',
    contactPerson: item?.contactPerson ?? item?.contacts ?? item?.contact ?? '',
    phone: item?.phone ?? item?.contactPhone ?? item?.mobile ?? item?.tel ?? '',
    email: item?.email ?? item?.mail ?? '',
    address: item?.address ?? item?.addr ?? '',
    remark: item?.remark ?? item?.remarks ?? '',
    customerType: normalizeCustomerType(item?.customerType ?? item?.type),
    createTime: formatDateTime(item?.createTime ?? item?.createDate ?? item?.createdAt ?? item?.gmtCreate),
  }
}

function extractRows(res) {
  if (Array.isArray(res?.rows)) return res.rows
  if (Array.isArray(res?.data?.rows)) return res.data.rows
  if (Array.isArray(res?.data?.list)) return res.data.list
  if (Array.isArray(res?.list)) return res.list
  if (Array.isArray(res?.data)) return res.data
  return []
}

function extractTotal(res, rows) {
  const totalValue = res?.total ?? res?.data?.total ?? res?.count ?? res?.data?.count
  const n = Number(totalValue)
  return Number.isFinite(n) ? n : rows.length
}

function buildListParams(overrides = {}) {
  const params = {
    pageNum: page.value,
    pageSize: pageSize.value,
    ...overrides,
  }
  if (!Object.prototype.hasOwnProperty.call(overrides, 'customerType') && typeFilter.value) {
    params.customerType = typeFilter.value
  }
  if (!Object.prototype.hasOwnProperty.call(overrides, 'customerName') && searchKeyword.value) {
    params.customerName = searchKeyword.value
  }
  return params
}

async function fetchList() {
  listLoading.value = true
  try {
    const res = await customerApi.list(buildListParams())
    const rows = extractRows(res)
    customerList.value = rows.map((item, index) => normalizeCustomerRow(item, index))
    total.value = extractTotal(res, rows)
  } catch (error) {
    customerList.value = []
    total.value = 0
    eui.showToast('获取客户列表失败，请稍后重试', 'error')
  } finally {
    listLoading.value = false
  }
}

function handleSearch() {
  searchKeyword.value = String(searchInput.value ?? '').trim()
  page.value = 1
  fetchList()
}

function resetFilters() {
  typeFilter.value = ''
  searchInput.value = ''
  searchKeyword.value = ''
  page.value = 1
  fetchList()
}

function buildExportParams() {
  const keyword = String(searchInput.value ?? '').trim()
  searchKeyword.value = keyword
  const params = {}
  if (typeFilter.value) params.customerType = typeFilter.value
  if (keyword) params.customerName = keyword
  return params
}

async function handleExport() {
  if (exportLoading.value) return
  exportLoading.value = true
  try {
    const blob = await customerApi.exportCustomer(buildExportParams())
    const url = window.URL.createObjectURL(new Blob([blob]))
    const link = document.createElement('a')
    link.href = url
    const timestamp = dayjs().format('YYYYMMDD_HHmmss')
    link.setAttribute('download', `客户信息_${timestamp}.xlsx`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  } catch (error) {
    eui.showToast('导出客户信息失败，请稍后重试', 'error')
  } finally {
    exportLoading.value = false
  }
}

function resetForm() {
  Object.assign(form, defaultForm())
  editingCustomerId.value = null
}

function fillFormFromRow(row = {}) {
  form.customerType = normalizeCustomerType(row.customerType ?? row.type)
  form.customerName = row.name ?? row.customerName ?? row.customer_name ?? ''
  form.shortName = row.shortName ?? row.customerShortName ?? row.abbr ?? ''
  form.contactPerson = row.contactPerson ?? row.contacts ?? row.contact ?? ''
  form.phone = row.phone ?? row.contactPhone ?? row.mobile ?? row.tel ?? ''
  form.email = row.email ?? row.mail ?? ''
  form.address = row.address ?? row.addr ?? ''
  form.remark = row.remark ?? row.remarks ?? ''
}

function openAddDialog() {
  dialogMode.value = 'add'
  resetForm()
  formDialogVisible.value = true
}

function openEditDialog(row) {
  dialogMode.value = 'edit'
  resetForm()
  editingCustomerId.value = row?.id ?? null
  fillFormFromRow(row)
  formDialogVisible.value = true
}

function closeFormDialog() {
  formDialogVisible.value = false
  resetForm()
}

function buildFormPayload() {
  const payload = {
    customerType: form.customerType,
    customerName: form.customerName.trim(),
    shortName: form.shortName.trim(),
    contactPerson: form.contactPerson.trim(),
    phone: form.phone.trim(),
    email: form.email.trim(),
    address: form.address.trim(),
    remark: form.remark.trim(),
  }
  if (dialogMode.value === 'edit' && editingCustomerId.value != null && editingCustomerId.value !== '') {
    payload.id = editingCustomerId.value
  }
  return payload
}

function validateForm() {
  if (!form.customerType) {
    eui.showToast('请选择客户类型', 'error')
    return false
  }
  if (!form.customerName.trim()) {
    eui.showToast('请输入客户名称', 'error')
    return false
  }
  return true
}

async function submitForm() {
  if (!validateForm()) return
  formSubmitting.value = true
  try {
    if (dialogMode.value === 'edit') {
      await customerApi.update(buildFormPayload())
      eui.showToast('修改客户成功', 'success')
    } else {
      await customerApi.add(buildFormPayload())
      eui.showToast('新增客户成功', 'success')
    }
    closeFormDialog()
    page.value = 1
    await fetchList()
  } catch (error) {
    eui.showToast(error?.msg || error?.message || (dialogMode.value === 'edit' ? '修改客户失败，请稍后重试' : '新增客户失败，请稍后重试'), 'error')
  } finally {
    formSubmitting.value = false
  }
}

const deleteDialogMessage = computed(() => {
  if (!deleteTarget.value) return '确认删除该客户吗？'
  return `确认删除客户“${deleteTarget.value.name || '—'}”吗？`
})

function handleDelete(row) {
  deleteTarget.value = row
  deleteDialogVisible.value = true
}

function closeDeleteDialog() {
  deleteDialogVisible.value = false
  deleteTarget.value = null
}

async function doDelete() {
  const id = deleteTarget.value?.id
  if (id == null || id === '') {
    eui.showToast('缺少客户标识，无法删除', 'error')
    return
  }
  deleteSubmitting.value = true
  try {
    await customerApi.del(id)
    eui.showToast('删除客户成功', 'success')
    closeDeleteDialog()
    if (customerList.value.length === 1 && page.value > 1) page.value -= 1
    await fetchList()
  } catch (error) {
    eui.showToast('删除客户失败，请稍后重试', 'error')
  } finally {
    deleteSubmitting.value = false
  }
}

function openDetail(row) {
  detailTarget.value = row
  detailDialogVisible.value = true
}

function closeDetailDialog() {
  detailDialogVisible.value = false
  detailTarget.value = null
}

onMounted(() => {
  fetchList()
})
</script>

<style scoped lang="scss">
.customers-page {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  box-sizing: border-box;
}

.customers-card {
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
}

.search-item {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;

  .search-label {
    flex-shrink: 0;
    white-space: nowrap;
    font-size: 14px;
    color: rgba(0, 0, 0, 0.7);
    text-align: right;
    min-width: 64px;
  }

  .search-input {
    width: 260px;
  }

  .search-input--select {
    width: 220px;
  }
}

.search-actions {
  flex-wrap: wrap;
  justify-content: flex-end;
}

.toolbar-actions {
  margin-left: auto;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.action-buttons {
  display: flex;
  align-items: center;
  justify-content: center;
}

.action-buttons :deep(.v-btn) {
  min-width: auto;
  padding-inline: 8px;
  white-space: nowrap;
}

.table-wrap {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.customers-table-host {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.customers-table {
  flex: 1 1 0;
  min-height: 0;
  width: 100%;
}

.customers-table :deep(.v-table__wrapper) {
  overflow: auto;
}

.customers-table :deep(.v-table__wrapper > table) {
  width: 100%;
  min-width: max-content;
}

.customers-table :deep(thead th) {
  background: rgba(255, 255, 255, 0.96) !important;
}

.customers-table :deep(th.sticky-action-col),
.customers-table :deep(td.sticky-action-col) {
  position: sticky;
  right: 0;
  white-space: nowrap;
  min-width: 120px;
  width: 120px;
  box-sizing: border-box;
  border-left: 1px solid var(--color-td-border, rgba(0, 0, 0, 0.08));
  box-shadow: -8px 0 14px -10px rgba(0, 0, 0, 0.18);
}

.customers-table :deep(th.sticky-action-col) {
  z-index: 4;
  background-color: #fff !important;
}

.customers-table :deep(td.sticky-action-col) {
  z-index: 1;
  background-color: #fff;
}

.customers-table :deep(tbody tr:hover td.sticky-action-col) {
  background-color: #fff;
  background-image: linear-gradient(90deg, rgba(38, 85, 241, 0.06) 0%, rgba(85, 7, 253, 0.06) 100%);
}

.empty-tip--fill {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.customers-pagination {
  :deep(.table-pagination) {
    padding-left: 0;
    padding-right: 0;
  }
}

.cell-name {
  max-width: 320px;
  font-weight: 500;
}

.cell-contact {
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cell-email {
  max-width: 180px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cell-address {
  max-width: 280px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}

.dialog-card {
  border-radius: 30px !important;
  background: #fff;
}

.gm-dialog-header {
  background: linear-gradient(185deg, #2655f160 0%, #fff0 60%);
  position: relative;

  .text-h5 {
    font-size: 24px !important;
    letter-spacing: 0.5px;
    font-weight: 500;
  }
}

.gm-btn-primary,
.gm-btn-secondary {
  height: 42px !important;
  border-radius: 12px !important;
  font-size: 16px !important;
}

.form-grid-2 {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 20px;
}

.form-group {
  min-width: 0;
}

.form-group.grid-full {
  grid-column: 1 / -1;
}

.form-group label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 8px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.72);
}

.required-star {
  color: #ff4d4f;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 20px;
}

.detail-item {
  min-width: 0;
}

.detail-item--full {
  grid-column: 1 / -1;
}

.detail-label {
  margin-bottom: 8px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.5);
}

.detail-value {
  min-height: 44px;
  padding: 12px 14px;
  border-radius: 16px;
  background: #F7FAFF;
  border: 1px solid #E3EAF8;
  color: #2E2E2E;
  display: flex;
  align-items: center;
  word-break: break-word;
}

.detail-value--multiline {
  align-items: flex-start;
  line-height: 1.7;
}

@media (max-width: 960px) {
  .detail-grid {
    grid-template-columns: 1fr;
  }
}
</style>
